

import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, Firestore, getFirestore, initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

import { firebaseConfig } from '@/firebase/config';

// IMPORTANT: DO NOT MODIFY THIS FUNCTION
export function initializeFirebase() {
  if (!getApps().length) {
    // Important! initializeApp() is called without any arguments because Firebase App Hosting
    // integrates with the initializeApp() function to provide the environment variables needed to
    // populate the FirebaseOptions in production. It is critical that we attempt to call initializeApp()
    // without arguments.
    let firebaseApp;
    try {
      // Check if we have explicit config populated
      const hasConfig = !!(firebaseConfig.projectId && firebaseConfig.apiKey);

      if (hasConfig) {
        // Prefer explicit config if available to avoid "no-options" warning on custom domains
        firebaseApp = initializeApp(firebaseConfig);
      } else {
        // Fallback to automatic initialization (App Hosting)
        firebaseApp = initializeApp();
      }
    } catch (e) {
      if (import.meta.env.PROD) {
        console.warn('Firebase: Initialization failed, attempting fallback with config.', e);
      }
      firebaseApp = initializeApp(firebaseConfig);
    }

    return getSdks(firebaseApp);
  }

  // If already initialized, return the SDKs with the already initialized App
  return getSdks(getApp());
}

let firestoreInstance: Firestore | null = null;

export function getSdks(firebaseApp: FirebaseApp) {
  if (typeof window !== 'undefined') {
    if (!firestoreInstance) {
      try {
        firestoreInstance = initializeFirestore(firebaseApp, {
          localCache: memoryLocalCache(),
        });
      } catch (err) {
        console.warn('Firestore already initialized or failed with options, falling back to getFirestore', err);
        firestoreInstance = getFirestore(firebaseApp);
      }
    }
  } else {
    // On server, always use getFirestore (standard behavior)
    firestoreInstance = getFirestore(firebaseApp);
  }

  if (typeof window !== 'undefined') {
    try {
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      if (siteKey) {
        initializeAppCheck(firebaseApp, {
          provider: new ReCaptchaV3Provider(siteKey),
          isTokenAutoRefreshEnabled: true,
        });
      }
    } catch (err) {
      // App Check might already be initialized or failed
      console.warn('App Check initialization skipped or failed', err);
    }
  }

  const functions = getFunctions(firebaseApp);
  const auth = getAuth(firebaseApp);
  const storage = getStorage(firebaseApp);

  // Dynamically connect to emulators if VITE_USE_EMULATORS is true
  if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectFunctionsEmulator(functions, 'localhost', 5001);

    // Connect Firestore if instance is available
    if (firestoreInstance) {
      connectFirestoreEmulator(firestoreInstance, 'localhost', 8080);
    }

    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectStorageEmulator(storage, 'localhost', 9199);

    console.warn("🚀 Connected to Firebase Emulators (Functions:5001, Firestore:8080, Auth:9099, Storage:9199)");
  }

  return {
    firebaseApp,
    auth,
    firestore: firestoreInstance,
    storage,
    functions,
  };
}

export * from './client-provider';
export * from './account-actions';
export * from './auth-policy';
export * from './error-emitter';
export * from './errors';
export * from './firestore/cache';
export * from './firestore/use-collection';
export * from './firestore/use-doc';
export * from './non-blocking-login';
export * from './non-blocking-updates';
export * from './provider';
export * from './storage';
